// OCTAGON INFINITY PRIVACY POLICY CONTENT
// Edit this file to update the privacy policy content

export const privacyPolicyContent = {
  title: "Privacy Policy",
  subtitle: "Octagon Infinity",
  effectiveDate: "Effective Date: November 14, 2025",
  lastUpdated: "Last Updated: November 14, 2025",
  
  sections: [
    {
      id: "introduction",
      title: "1. Introduction",
      content: `Welcome to Octagon Infinity ("we," "our," or "us"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website octagon8.io and use our NFT-based real estate investment platform (collectively, the "Services").

Please read this Privacy Policy carefully. By accessing or using our Services, you acknowledge that you have read, understood, and agree to be bound by all the terms of this Privacy Policy. If you do not agree with the terms of this Privacy Policy, please do not access or use our Services.`
    },
    
    {
      id: "information-collection",
      title: "2. Information We Collect",
      content: `We collect information that you provide directly to us, information we obtain automatically when you use our Services, and information from third-party sources.

2.1 Information You Provide to Us:
• Personal Identification Information: Name, email address, postal address, phone number, date of birth
• Financial Information: Cryptocurrency wallet addresses, transaction history, investment preferences
• Identity Verification: Government-issued ID, proof of address, and other KYC/AML documentation
• Account Credentials: Username, password, security questions
• Communication Data: Messages, inquiries, and correspondence with us

2.2 Automatically Collected Information:
• Device Information: IP address, browser type, operating system, device identifiers
• Usage Data: Pages visited, time spent, click patterns, referring/exit pages
• Location Data: General geographic location based on IP address
• Cookies and Tracking Technologies: Session data, preferences, analytics

2.3 Blockchain Data:
• Public blockchain transactions and wallet addresses
• NFT ownership and transfer records
• Smart contract interactions`
    },
    
    {
      id: "use-of-information",
      title: "3. How We Use Your Information",
      content: `We use the collected information for various purposes:

3.1 Service Delivery:
• Process NFT purchases and manage your VIP Pass
• Facilitate real estate investments and profit distributions
• Manage your account and provide customer support
• Send transactional emails and important notices

3.2 Legal and Compliance:
• Comply with KYC/AML requirements and regulations
• Prevent fraud, money laundering, and illegal activities
• Enforce our Terms of Service and legal obligations
• Respond to legal requests and prevent harm

3.3 Platform Improvement:
• Analyze usage patterns and improve our Services
• Develop new features and functionality
• Conduct research and analytics
• Personalize user experience

3.4 Marketing and Communications:
• Send newsletters and promotional materials (with your consent)
• Notify you about new projects and investment opportunities
• Conduct surveys and gather feedback`
    },
    
    {
      id: "information-sharing",
      title: "4. Information Sharing and Disclosure",
      content: `We may share your information in the following circumstances:

4.1 Service Providers:
We engage third-party companies and individuals to perform services on our behalf, including:
• Payment processing and cryptocurrency transactions
• Identity verification and KYC/AML services
• Cloud storage and hosting providers
• Email delivery and communication services
• Analytics and performance monitoring

4.2 Legal Requirements:
We may disclose your information if required to do so by law or in response to:
• Court orders or legal processes
• Government or regulatory requests
• Protection of our rights and property
• Investigation of fraud or security issues

4.3 Business Transfers:
In the event of a merger, acquisition, reorganization, or sale of assets, your information may be transferred to the successor entity.

4.4 Consent:
We may share your information with your explicit consent for specific purposes not covered in this Policy.

We do not sell your personal information to third parties for their marketing purposes.`
    },
    
    {
      id: "data-security",
      title: "5. Data Security",
      content: `We implement appropriate technical and organizational security measures to protect your personal information:

• Encryption of sensitive data in transit and at rest
• Secure socket layer (SSL) technology for data transmission
• Multi-factor authentication for account access
• Regular security audits and vulnerability assessments
• Restricted access to personal information on a need-to-know basis
• Employee training on data protection and privacy

However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.`
    },
    
    {
      id: "data-retention",
      title: "6. Data Retention",
      content: `We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.

• Account Information: Retained while your account is active and for a period thereafter as required by law
• Transaction Records: Retained for tax, accounting, and regulatory compliance purposes (typically 7-10 years)
• Marketing Communications: Retained until you unsubscribe or request deletion
• KYC/AML Documentation: Retained as required by applicable regulations

After the retention period expires, we will securely delete or anonymize your personal information.`
    },
    
    {
      id: "your-rights",
      title: "7. Your Privacy Rights",
      content: `Depending on your jurisdiction, you may have the following rights regarding your personal information:

7.1 Access and Portability:
• Right to access your personal information we hold
• Right to receive a copy of your data in a portable format

7.2 Correction and Update:
• Right to correct inaccurate or incomplete information
• Right to update your personal details

7.3 Deletion:
• Right to request deletion of your personal information (subject to legal obligations)
• Right to be forgotten (where applicable under GDPR)

7.4 Restriction and Objection:
• Right to restrict processing of your information
• Right to object to certain types of processing

7.5 Withdrawal of Consent:
• Right to withdraw consent for marketing communications
• Right to opt-out of non-essential data processing

To exercise these rights, please contact us at privacy@octagon8.io.`
    },
    
    {
      id: "cookies",
      title: "8. Cookies and Tracking Technologies",
      content: `We use cookies and similar tracking technologies to enhance your experience on our website:

8.1 Types of Cookies:
• Essential Cookies: Necessary for website functionality
• Performance Cookies: Help us understand how visitors use our site
• Functional Cookies: Remember your preferences and settings
• Analytics Cookies: Collect data on site usage and performance

8.2 Managing Cookies:
You can control cookies through your browser settings. However, disabling certain cookies may affect the functionality of our Services.

8.3 Third-Party Analytics:
We use third-party analytics services (such as Google Analytics) to understand user behavior. These services may use cookies and collect data according to their own privacy policies.`
    },
    
    {
      id: "international-transfers",
      title: "9. International Data Transfers",
      content: `Octagon Infinity operates globally, and your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws that differ from those in your jurisdiction.

When we transfer personal information internationally, we ensure appropriate safeguards are in place, including:
• Standard contractual clauses approved by the European Commission
• Adequacy decisions by relevant authorities
• Your explicit consent for the transfer

We are committed to ensuring that your information receives adequate protection regardless of where it is processed.`
    },
    
    {
      id: "children-privacy",
      title: "10. Children's Privacy",
      content: `Our Services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children under 18 years of age.

If you are a parent or guardian and believe that your child has provided us with personal information, please contact us immediately at privacy@octagon8.io. We will take steps to remove such information from our systems.

Users must be of legal age in their jurisdiction to enter into investment contracts and participate in our platform.`
    },
    
    {
      id: "third-party-links",
      title: "11. Third-Party Links and Services",
      content: `Our Services may contain links to third-party websites, applications, and services that are not operated by us. This Privacy Policy does not apply to such third-party services.

We are not responsible for the privacy practices of third parties. We encourage you to review the privacy policies of any third-party services before providing them with your personal information.

Third-party services we may integrate with include:
• Cryptocurrency wallets and exchanges
• Blockchain explorers
• Social media platforms
• Analytics providers`
    },
    
    {
      id: "policy-changes",
      title: "12. Changes to This Privacy Policy",
      content: `We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors.

When we make material changes to this Privacy Policy, we will:
• Update the "Last Updated" date at the top of this Policy
• Notify you via email or through a prominent notice on our website
• Request your consent if required by applicable law

We encourage you to review this Privacy Policy periodically to stay informed about how we protect your information. Your continued use of our Services after changes to this Privacy Policy constitutes acceptance of the updated terms.`
    },
    
    {
      id: "contact-us",
      title: "13. Contact Information",
      content: `If you have any questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us:

Email: privacy@octagon8.io
General Inquiries: info@octagon8.io
Support: support@octagon8.io

Mailing Address:
Octagon Infinity
[Company Address]
[City, Country, Postal Code]

Data Protection Officer:
For GDPR-related inquiries, you may contact our Data Protection Officer at dpo@octagon8.io.

We will respond to your inquiry within a reasonable timeframe, typically within 30 days.`
    }
  ]
};
